import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { profile } from '../data/profile';
import { scrollToTarget } from '../lib/lenis';
import Magnetic from './Magnetic';
import './Navigation.css';

const navItems = [
  { name: 'Home', to: '/', type: 'route' },
  { name: 'About', to: '#about', type: 'anchor' },
  { name: 'Experience', to: '#experience', type: 'anchor' },
  { name: 'Work', to: '/projects', type: 'route' },
  { name: 'Contact', to: '#contact', type: 'anchor' },
];

const Navigation = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goAnchor = (hash) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: hash } });
    } else {
      scrollToTarget(hash);
    }
  };

  const handleClick = (item) => {
    if (item.type === 'anchor') {
      goAnchor(item.to);
    } else if (item.to === location.pathname) {
      scrollToTarget(0, { offset: 0 });
    } else {
      navigate(item.to);
    }
    setOpen(false);
  };

  const isActive = (item) => {
    if (item.type !== 'route') return false;
    if (item.to === '/') return location.pathname === '/';
    return location.pathname.startsWith(item.to) || location.pathname.startsWith('/project/');
  };

  return (
    <motion.nav
      className={`nav ${scrolled ? 'nav--scrolled' : ''}`}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className="nav__inner container">
        <button
          className="nav__logo"
          onClick={() => {
            if (location.pathname === '/') scrollToTarget(0, { offset: 0 });
            else navigate('/');
            setOpen(false);
          }}
          aria-label="Home"
        >
          <img className="nav__logo-img" src={`${process.env.PUBLIC_URL}${profile.avatar}`} alt={profile.name} />
          <span className="nav__logo-text">Shaik&nbsp;Hatim</span>
        </button>

        <div className="nav__links">
          {navItems.map((item) => (
            <button
              key={item.name}
              className={`nav__link ${isActive(item) ? 'nav__link--active' : ''}`}
              onClick={() => handleClick(item)}
            >
              {item.name}
              {isActive(item) && (
                <motion.span
                  className="nav__underline"
                  layoutId="nav-underline"
                  transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                />
              )}
            </button>
          ))}
          <Magnetic strength={0.25}>
            <button className="btn btn-primary nav__cta" onClick={() => goAnchor('#contact')}>
              Get in touch
            </button>
          </Magnetic>
        </div>

        <button className="nav__toggle" onClick={() => setOpen((o) => !o)} aria-label="Menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="nav__mobile"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
          >
            {navItems.map((item) => (
              <button key={item.name} className="nav__mobile-link" onClick={() => handleClick(item)}>
                {item.name}
              </button>
            ))}
            <button className="btn btn-primary nav__mobile-cta" onClick={() => handleClick({ type: 'anchor', to: '#contact' })}>
              Get in touch
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navigation;
